import { useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AttachMoney as BudgetIcon,
  WorkspacePremium as VerifiedIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getExchanges, getUserProfile, submitExchangeUID, uploadScreenshot } from '@/services/api';
import { MetricCard, PageShell, SectionHeader } from '@/components/common/Page';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { useTelegram } from '@/contexts/TelegramContext';
import ExchangeLogo from '@/components/common/ExchangeLogo';
import LanguageSelector from '@/components/common/LanguageSelector';
import type { Exchange, UserExchange } from '@/types';
import { formatUsd } from '@/utils/format';

export default function ProfilePage() {
  const queryClient = useQueryClient();
  const { user, isAdmin } = useAuth();
  const { t } = useI18n();
  const { isTelegram } = useTelegram();

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [selectedExchange, setSelectedExchange] = useState('');
  const [exchangeUID, setExchangeUID] = useState('');
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [uploadingScreenshot, setUploadingScreenshot] = useState<number | null>(null);
  const [openPrivateAccessDialog, setOpenPrivateAccessDialog] = useState(false);

  const { data: profileData, isLoading } = useQuery({
    queryKey: ['user-profile'],
    queryFn: getUserProfile,
  });

  const { data: exchangesData } = useQuery({
    queryKey: ['exchanges'],
    queryFn: getExchanges,
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      const result = await submitExchangeUID(selectedExchange, exchangeUID);
      if (screenshotFile && result.verification?.id) {
        await uploadScreenshot(result.verification.id, screenshotFile);
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      setOpenAddDialog(false);
      setSelectedExchange('');
      setExchangeUID('');
      setScreenshotFile(null);
      setSubmitError(null);
    },
    onError: () => {
      setSubmitError(t('profile.submitExchangeError'));
    },
  });

  const availableExchanges =
    exchangesData?.exchanges.filter((exchange: Exchange) => {
      const existing = profileData?.exchanges?.find(
        (userExchange: UserExchange) => userExchange.exchange_id === exchange.id,
      );
      return !existing || existing.verification_status === 'rejected';
    }) || [];

  const handleAddExchange = () => {
    setSubmitError(null);
    submitMutation.mutate();
  };

  const handleScreenshotUpload = async (verificationId: number, file: File) => {
    setUploadingScreenshot(verificationId);
    try {
      await uploadScreenshot(verificationId, file);
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    } catch (error) {
      console.error('Screenshot upload failed:', error);
    } finally {
      setUploadingScreenshot(null);
    }
  };

  const getStatusChip = (status: UserExchange['verification_status']) => {
    const config = {
      verified: { label: t('status.verified'), color: 'success' as const },
      pending: { label: t('status.pending'), color: 'warning' as const },
      rejected: { label: t('status.rejected'), color: 'error' as const },
    }[status];

    return <Chip label={config.label} color={config.color} size="small" />;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageShell
      eyebrow={t('profile.eyebrow')}
      title={t('profile.title')}
      showAnnouncements
      action={
        availableExchanges.length > 0 ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAddDialog(true)}>
            {t('profile.addExchange')}
          </Button>
        ) : undefined
      }
    >
      <Card sx={{ position: 'relative', mb: 2 }}>
        <CardContent>
          <Box sx={{ position: 'absolute', top: 18, right: 18 }}>
            <LanguageSelector />
          </Box>

          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, pr: 6 }}>
            <Avatar
              src={user?.photo_url || undefined}
              alt={[user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.telegram_username || 'Profile'}
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontWeight: 800,
                fontSize: '1.5rem',
              }}
            >
              {user?.first_name?.[0] || user?.telegram_username?.[0] || '?'}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5">
                {user?.first_name} {user?.last_name}
              </Typography>
              {user?.telegram_username && (
                <Typography variant="body2" color="text.secondary">
                  @{user.telegram_username}
                </Typography>
              )}
            </Box>
          </Stack>

          <Stack direction="row" flexWrap="wrap" gap={1}>
            <Chip
              label={t('profile.access', {
                level: user?.access_level === 'private' ? t('common.private') : t('common.basic'),
              })}
              color={user?.access_level === 'private' ? 'success' : 'default'}
            />
            {isAdmin && <Chip label={t('common.admin')} color="primary" />}
            {!isTelegram && <Chip label={t('common.devMode')} color="warning" variant="outlined" />}
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <MetricCard
            label={t('profile.verifiedExchanges')}
            value={profileData?.verifiedExchangeCount || 0}
            icon={<VerifiedIcon fontSize="small" />}
            tone="success"
          />
        </Grid>
        <Grid item xs={6}>
          <MetricCard
            label={t('profile.budget')}
            value={formatUsd(user?.budget || 0)}
            icon={<BudgetIcon fontSize="small" />}
            tone="secondary"
          />
        </Grid>
      </Grid>

      <SectionHeader title={t('profile.exchangeVerifications')} />

      {profileData?.exchanges && profileData.exchanges.length > 0 ? (
        <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {profileData.exchanges.map((exchange: UserExchange) => (
            <Card key={exchange.id}>
              <ListItem sx={{ alignItems: 'flex-start', py: 1.5 }}>
                <Box sx={{ mr: 1.5, mt: 0.25 }}>
                  <ExchangeLogo
                    src={exchange.logo_url}
                    name={exchange.exchange_name}
                    id={exchange.exchange_id}
                    size={34}
                  />
                </Box>
                <ListItemText
                  primary={
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                      <Typography>{exchange.exchange_name || exchange.exchange_id}</Typography>
                      {getStatusChip(exchange.verification_status)}
                    </Stack>
                  }
                  secondary={
                    <Box sx={{ mt: 0.75 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('profile.uid', { uid: exchange.exchange_uid })}
                      </Typography>
                      {exchange.screenshot_url ? (
                        <Chip label={t('profile.screenshotUploaded')} size="small" color="info" sx={{ mt: 0.75 }} />
                      ) : exchange.verification_status === 'pending' ? (
                        <Button
                          size="small"
                          variant="outlined"
                          component="label"
                          disabled={uploadingScreenshot === exchange.id}
                          sx={{ mt: 1 }}
                        >
                          {uploadingScreenshot === exchange.id ? t('common.uploading') : t('profile.uploadScreenshot')}
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(event) => {
                              const file = event.target.files?.[0];
                              if (file) {
                                handleScreenshotUpload(exchange.id, file);
                              }
                            }}
                          />
                        </Button>
                      ) : null}
                      {exchange.rejection_reason && (
                        <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                          {t('profile.reason', { reason: exchange.rejection_reason })}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            </Card>
          ))}
        </List>
      ) : (
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography color="text.secondary" gutterBottom>
              {t('profile.noExchanges')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAddDialog(true)}
              disabled={availableExchanges.length === 0}
            >
              {t('profile.addFirstExchange')}
            </Button>
          </CardContent>
        </Card>
      )}

      {user?.access_level === 'basic' && (
        <Card sx={{ mt: 2, borderColor: 'secondary.main' }}>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('profile.privateAccess')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('profile.privateAccessHint')}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<VerifiedIcon />}
                onClick={() => setOpenPrivateAccessDialog(true)}
                sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, flexShrink: 0 }}
              >
                {t('profile.requestPrivateAccess')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Dialog open={openPrivateAccessDialog} onClose={() => setOpenPrivateAccessDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('profile.privateAccessRequestTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
            {t('profile.privateAccessRequestBody')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPrivateAccessDialog(false)}>{t('common.close')}</Button>
          <Button
            variant="contained"
            component="a"
            href="https://t.me/admin"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('profile.contactManager')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('profile.addExchangeVerification')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('profile.enterUidAndScreenshot')}
            </Typography>

            <FormControl fullWidth>
              <InputLabel>{t('profile.exchange')}</InputLabel>
              <Select
                value={selectedExchange}
                label={t('profile.exchange')}
                onChange={(event) => setSelectedExchange(event.target.value)}
              >
                {availableExchanges.map((exchange: Exchange) => (
                  <MenuItem key={exchange.id} value={exchange.id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ExchangeLogo src={exchange.logo_url} name={exchange.name} id={exchange.id} size={24} />
                      <Typography>{exchange.name}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t('profile.exchangeUid')}
              placeholder={t('profile.exchangeUidPlaceholder')}
              value={exchangeUID}
              onChange={(event) => setExchangeUID(event.target.value)}
            />

            <Box>
              <Button
                variant={screenshotFile ? 'contained' : 'outlined'}
                component="label"
                fullWidth
                color={screenshotFile ? 'success' : 'primary'}
              >
                {screenshotFile ? screenshotFile.name : t('profile.chooseScreenshot')}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                      setScreenshotFile(file);
                    }
                  }}
                />
              </Button>
              <Typography variant="caption" color="text.secondary">
                {t('profile.screenshotMustShowUid')}
              </Typography>
            </Box>

            {submitError && <Alert severity="error">{submitError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleAddExchange}
            disabled={submitMutation.isPending || !selectedExchange || !exchangeUID || !screenshotFile}
          >
            {submitMutation.isPending ? t('common.submitting') : t('common.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
